/*
  Colour demo for the custom colour product.

  An empty board tells a first-time shopper nothing, so until they commit we show the thing
  working: a colour slides in from the side, scales up, bounces onto the board, and the board
  fills with it from the bottom like it is being poured in. On a double sided variant the
  board then turns and the next colour arrives from the other side to fill the back, which is
  the whole point of that option and the hardest part to put into words.

  The demo paints a wash layer inside each face, never setFaceColor. That keeps it strictly
  cosmetic: no chip, no step value, no line item property, nothing that could be mistaken for
  a choice the shopper made. The moment they pick a colour, or grab the board themselves, the
  wash drains and the demo is done for good.

  Colours come from the product's custom.preview_colors metafield, via data-colors.
*/
(function () {
  'use strict';

  // One pass, in milliseconds.
  const FLY = 820;      // travelling in from the side, growing as it comes
  const TUCK = 320;     // the last of the way, sliding in behind the board
  const FADE = 480;     // board taking the colour on
  const HOLD = 1150;    // long enough to register
  const TURN = 900;     // board turning to the other side
  const CLEAR = 460;    // colour fading back off
  const GAP = 420;
  // Minimum perceptual distance (CIE76 dE). Two sides of one backdrop have to read as
  // obviously different, and each pass has to look different from the one before it.
  const PAIR_MIN_DE = 60;
  const RUN_MIN_DE = 45;


  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Colours are compared in Lab, not by hex or hue. The curated list is grouped by family -
  // sand sits beside taupe, teal beside emerald - so walking it in order pairs shades that
  // are plainly different values and still look like the same colour on the board.
  function channel(c) {
    const v = c / 255;
    return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  }

  function toLab(hex) {
    const v = String(hex || '').replace('#', '');
    if (v.length !== 6) return [0, 0, 0];
    const r = channel(parseInt(v.slice(0, 2), 16));
    const g = channel(parseInt(v.slice(2, 4), 16));
    const b = channel(parseInt(v.slice(4, 6), 16));
    const f = (t) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
    const x = f((0.4124 * r + 0.3576 * g + 0.1805 * b) / 0.95047);
    const y = f(0.2126 * r + 0.7152 * g + 0.0722 * b);
    const z = f((0.0193 * r + 0.1192 * g + 0.9505 * b) / 1.08883);
    return [116 * y - 16, 500 * (x - y), 200 * (y - z)];
  }

  function distance(a, b) {
    const p = toLab(a);
    const q = toLab(b);
    return Math.sqrt((p[0] - q[0]) ** 2 + (p[1] - q[1]) ** 2 + (p[2] - q[2]) ** 2);
  }

  // Artwork brightness, measured rather than assumed: forcing white over every design
  // leaves the placeholder unreadable on the pale ones.
  //
  // Sampled over the band the placeholder text actually occupies, not the centre of the
  // image - "Side one" sits around 30-50% of the height and the material label around 80%,
  // so a centre sample misses the lower label entirely.
  //
  // Shopify's CDN is same-origin here, so the canvas stays untainted; anything that does
  // throw simply leaves the text dark.
  const TEXT_IS_DARK_BELOW = 150;
  const lumCache = {};

  function artLuminance(url) {
    if (url in lumCache) return Promise.resolve(lumCache[url]);
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        try {
          const W = 32;
          const H = 48;
          const c = document.createElement('canvas');
          c.width = W;
          c.height = H;
          const ctx = c.getContext('2d');
          ctx.drawImage(img, 0, 0, W, H);
          const x = Math.round(W * 0.1);
          const y = Math.round(H * 0.25);
          const d = ctx.getImageData(x, y, W - x * 2, Math.round(H * 0.63)).data;
          let sum = 0;
          for (let i = 0; i < d.length; i += 4) {
            sum += 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
          }
          const lum = sum / (d.length / 4);
          lumCache[url] = lum;
          resolve(lum);
        } catch (e) { resolve(null); }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
  }

  // Perceived brightness, the same rule the board uses to pick white or dark label text.
  function isDark(hex) {
    const v = String(hex || '').replace('#', '');
    if (v.length !== 6) return false;
    const r = parseInt(v.slice(0, 2), 16);
    const g = parseInt(v.slice(2, 4), 16);
    const b = parseInt(v.slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) < 128;
  }

  class MixMatchAmbient extends HTMLElement {
    connectedCallback() {
      this.flyer = this.querySelector('[data-mm-flyer]');
      this.colors = (this.dataset.colors || '').split(',').map((c) => c.trim()).filter(Boolean);
      this.designs = [];
      if (this.dataset.designs) {
        try {
          const parsed = JSON.parse(this.dataset.designs) || [];
          const seen = {};
          // the shop can hold more than one metaobject pointing at the same artwork
          this.designs = parsed.filter((d) => {
            if (!d || !d.image || seen[d.image]) return false;
            seen[d.image] = true;
            return true;
          });
        } catch (e) { this.designs = []; }
      }
      // Designs win where both are present: a Mix & Match board sells the artwork.
      this.mode = this.designs.length > 1 ? 'design' : 'color';
      this.items = this.mode === 'design' ? this.designs : this.colors;
      if (!this.flyer || this.items.length < 2) return;

      this.stage = this.closest('mix-match-board');
      this.board = this.stage && this.stage.querySelector('[data-mm-board]');
      this.washes = {
        front: this.stage && this.stage.querySelector('[data-mm-demo="front"]'),
        back: this.stage && this.stage.querySelector('[data-mm-demo="back"]'),
      };
      if (!this.board) return;

      this.forcedDouble = undefined;
      this.stopped = false;
      this.running = false;
      this.queue = null;
      this.timers = [];

      if (reduceMotion()) return; // the motion is the feature; skip it rather than fake it

      // Touching the board is the shopper taking over. Stop competing with them.
      this.onGrab = () => this.stop();
      this.stage.addEventListener('pointerdown', this.onGrab);

      this.onVisibility = () => (document.hidden ? this.pause() : this.resume());
      document.addEventListener('visibilitychange', this.onVisibility);
      this.observer = new IntersectionObserver((entries) => {
        this.onScreen = entries[0].isIntersecting;
        if (this.onScreen) this.resume();
        else this.pause();
      }, { threshold: 0.25 });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.clearTimers();
      if (this.observer) this.observer.disconnect();
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.onGrab && this.stage) this.stage.removeEventListener('pointerdown', this.onGrab);
    }

    // ----- plumbing ----------------------------------------------------------
    clearTimers() {
      this.timers.forEach((t) => window.clearTimeout(t));
      this.timers = [];
    }

    after(ms, fn) {
      this.timers.push(window.setTimeout(fn, ms));
    }

    // The flyer has to arrive AS the board, so it borrows the board's live geometry rather
    // than guessing: same width, same proportions, same resting angle.
    matchBoard() {
      const rect = this.board.getBoundingClientRect();
      const ar = this.board.style.getPropertyValue('--mm-ar') || '2 / 3';
      this.flyer.style.setProperty('--fw', rect.width + 'px');
      this.flyer.style.setProperty('--mm-ar', ar);
      return rect;
    }

    placeFlyer(o) {
      const f = this.flyer.style;
      f.setProperty('--fdur', o.duration + 'ms');
      f.setProperty('--fease', o.ease || 'cubic-bezier(0.22, 0.68, 0.24, 1)');
      f.setProperty('--fx', o.x + 'px');
      f.setProperty('--fz', o.z + 'px');
      f.setProperty('--fs', o.scale);
      f.setProperty('--fry', o.ry + 'deg');
      f.setProperty('--fo', o.opacity);
      f.setProperty('--fodur', (o.fadeDuration === undefined ? o.duration : o.fadeDuration) + 'ms');
      if (this.mode === 'design') {
        f.setProperty('--fc', 'transparent');
        f.setProperty('--fart', o.item && o.item.image ? 'url("' + o.item.image + '")' : 'none');
      } else {
        f.setProperty('--fc', o.item || 'transparent');
        f.setProperty('--fart', 'none');
      }
    }

    // The board takes the colour, or the artwork, on.
    show(side, item, duration) {
      const wash = this.washes[side];
      if (!wash) return;
      const face = wash.closest('.mix-match-board__face');
      wash.style.setProperty('--demo-dur', duration + 'ms');
      if (this.mode === 'design') {
        wash.style.setProperty('--demo', 'transparent');
        wash.style.setProperty('--demo-art', 'url("' + item.image + '")');
        // Artwork resolves into place rather than dissolving: it arrives soft and bright
        // and sharpens, which reads as the design settling onto the board.
        wash.classList.add('is-arriving');
        window.setTimeout(() => wash.classList.remove('is-arriving'), 40);
        if (face) {
          face.classList.add('is-demo-art');
          // Sampling is async and a pass is short, so tag the request: without this a slow
          // sample can land after the next design has already taken the face.
          const token = (this.lumToken = (this.lumToken || 0) + 1);
          artLuminance(item.image).then((lum) => {
            if (lum === null || token !== this.lumToken) return;
            face.classList.toggle('is-demo-dark', lum < TEXT_IS_DARK_BELOW);
          });
        }
      } else {
        wash.style.setProperty('--demo', item);
        wash.style.setProperty('--demo-art', 'none');
        if (face) face.classList.toggle('is-demo-dark', isDark(item));
      }
      wash.style.setProperty('--demo-o', '1');
    }

    clear(duration) {
      ['front', 'back'].forEach((side) => {
        const wash = this.washes[side];
        if (!wash) return;
        wash.style.setProperty('--demo-dur', duration + 'ms');
        wash.style.setProperty('--demo-o', '0');
        const face = wash.closest('.mix-match-board__face');
        if (face) face.classList.remove('is-demo-dark', 'is-demo-art');
      });
    }

    // Designs carry their own orientation, so a landscape piece previews on a board turned
    // on its side instead of being cropped into a portrait panel. Set before the swoop, so
    // the board has finished resizing by the time the artwork lands - and before the turn
    // for the back, so it comes round already the right shape.
    applyOrientation(side, item) {
      if (this.mode !== 'design' || !item || !item.orientation) return;
      if (this.stage && this.stage.setDemoOrientation) {
        this.stage.setDemoOrientation(side, item.orientation);
      }
    }

    // ----- the pass ----------------------------------------------------------
    resume() {
      if (this.stopped || this.running || this.onScreen === false || document.hidden) return;
      this.running = true;
      this.run();
    }

    pause() {
      this.running = false;
      this.clearTimers();
    }

    // A shuffled queue, drained one colour at a time and reshuffled when empty. Scanning a
    // fixed list from a moving cursor is deterministic however the cursor is nudged, and
    // settles into a loop a handful of passes long; draining a shuffle covers the whole
    // palette before anything repeats.
    refill() {
      const pool = this.items.slice();
      for (let i = pool.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const t = pool[i]; pool[i] = pool[j]; pool[j] = t;
      }
      this.queue = pool;
    }

    // How unlike each other two entries are. Colours compare perceptually; designs only
    // have to be a different design, since two pieces of artwork never read as the same
    // the way two shades of beige do.
    apart(a, b) {
      if (this.mode === 'design') return a && b && a.image !== b.image ? Infinity : 0;
      return distance(a, b);
    }

    // First queued entry far enough from `from`. When the round runs low every remaining
    // one can be a near match, so start a fresh round rather than accept one - the whole
    // point is that no pass ever shows two shades of the same colour. Only a palette with
    // nothing distant at all falls back to the farthest available.
    pickFar(from, minDE) {
      if (!this.queue || !this.queue.length) this.refill();
      if (!from) return this.queue.shift();
      for (let round = 0; round < 2; round++) {
        let bestAt = 0;
        let bestD = -1;
        for (let i = 0; i < this.queue.length; i++) {
          const d = this.apart(from, this.queue[i]);
          if (d > bestD) { bestD = d; bestAt = i; }
          if (d >= minDE) return this.queue.splice(i, 1)[0];
        }
        if (round === 0) this.refill();
        else return this.queue.splice(bestAt, 1)[0];
      }
      return this.queue.shift();
    }

    // One colour: swoops in from deep in the scene, passes behind the board, and the board
    // takes the colour as it goes. The flyer never exceeds roughly half the board's width
    // and always sits behind it on Z, so it physically cannot stick out past the edges the
    // way a board-sized panel did.
    deliver(fromLeft, face, item, done) {
      const rect = this.matchBoard();
      const width = rect.width || 240;

      // far off and small, turned away
      this.placeFlyer({ x: fromLeft ? -width * 1.6 : width * 1.6, z: -760, scale: 0.34,
        ry: fromLeft ? 30 : -30, opacity: 0, duration: 0, item: item });

      // fade up quickly so it appears out in the room rather than blinking on
      this.after(40, () => {
        this.placeFlyer({ x: fromLeft ? -width * 1.45 : width * 1.45, z: -740, scale: 0.36,
          ry: fromLeft ? 29 : -29, opacity: 1, item: item, duration: 240 });
      });

      // the swoop: in toward centre, easing onto the board's angle, still well behind it
      this.after(40 + 200, () => {
        this.placeFlyer({ x: 0, z: -150, scale: 0.6, ry: -16, opacity: 1, item: item,
          duration: FLY, ease: 'cubic-bezier(0.3, 0.85, 0.35, 1)' });
      });

      // by now it is behind the board: hand over the colour and fade out unseen
      this.after(40 + 200 + FLY - 160, () => {
        this.show(face, item, FADE);
        this.placeFlyer({ x: 0, z: -150, scale: 0.6, ry: -16, opacity: 0, item: item,
          duration: TUCK, fadeDuration: TUCK });
      });

      this.after(40 + 200 + FLY + FADE, done);
    }

    run() {
      if (!this.running || this.stopped) return;

      // decided once, so a variant change mid pass cannot leave the board turned
      const double = this.isDouble();

      const finish = () => {
        if (this.stopped) return;
        this.clear(CLEAR);
        this.after(CLEAR, () => {
          if (double) this.stage.flip(false);
          this.after(double ? TURN : 0, () => this.after(GAP, () => this.run()));
        });
      };

      const front = this.pickFar(this.lastFront, RUN_MIN_DE);
      this.lastFront = front;

      const turnOver = () => {
        if (this.stopped) return;
        // the second one arrives from the other side, so the two reads stay distinct
        const back = this.pickFar(front, PAIR_MIN_DE);
        this.applyOrientation('back', back);
        this.stage.flip(true);
        this.after(TURN, () => this.deliver(false, 'back', back, () => this.after(HOLD, finish)));
      };

      this.applyOrientation('front', front);
      this.deliver(true, 'front', front, () => {
        this.after(HOLD, double ? turnOver : finish);
      });
    }

    // ----- outside control ---------------------------------------------------
    // The custom colour product has a Sides option and its picker tells us which way it is
    // set. Mix & Match has no such option - it is double sided by definition - and nothing
    // was telling the demo, so it only ever ran the front. Fall back to asking the board
    // whether it can turn at all.
    isDouble() {
      if (this.forcedDouble !== undefined) return this.forcedDouble;
      return !(this.stage && this.stage.classList.contains('mix-match-stage--no-flip'));
    }

    setDoubleSided(value) {
      this.forcedDouble = !!value;
    }

    // Their choice, their board. The demo is finished - called by the board itself the
    // moment any real design or colour lands, so it retires whichever picker made the pick.
    stop() {
      if (this.stopped) return;
      this.stopped = true;
      this.running = false;
      this.clearTimers();
      this.clear(CLEAR);
      // hand back any orientation the demo borrowed, leaving a chosen side alone
      if (this.stage && this.stage.clearDemoOrientation) this.stage.clearDemoOrientation();
      this.placeFlyer({ x: 0, z: 0, scale: 1, ry: -16, opacity: 0, duration: 200 });
      this.after(CLEAR + 60, () => { this.hidden = true; });
    }
  }

  if (!customElements.get('mix-match-ambient')) {
    customElements.define('mix-match-ambient', MixMatchAmbient);
  }
})();
